import { Trans, useLingui } from '@lingui/react/macro'
import CantileverButton from 'components/CantileverButton'
import {
  formatOverlayTimestamp,
  overlayForegroundMix,
  overlayPanelSurface,
} from 'features/timeSeries/components/chartOverlays/overlayChrome'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import type { VisibleSensorInfo } from 'features/timeSeries/components/rulerTool/useRulerTool'
import {
  LABEL_GROUPS,
  type LabelGroupId,
} from 'features/timeSeries/labels/labelGroups'
import { Plus } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { mobileInputTextStyles } from 'styles/mobileInputTextStyles'

import { buildPenScopeOptions } from './labelGeometry'
import type { LabelPanelState } from './useLabelTool'

/* ─── Styled components ─────────────────────────────────────────────────── */

const PanelAnchor = styled.div`
  position: absolute;
  right: 0.5rem;
  bottom: 0.5rem;
  z-index: 35;
`

const Panel = styled.div`
  ${overlayPanelSurface}
  width: 260px;
  padding: 8px 10px 10px;
  font-family: 'Manrope', sans-serif;
  font-size: 11px;
`

const PanelHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 6px;
`

const PanelTitle = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: var(--foreground);
`

const TimeRange = styled.div`
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 9px;
  color: var(--muted-foreground);
  line-height: 1.4;
  margin-bottom: 8px;
`

const FieldLabel = styled.label`
  display: block;
  font-size: 10px;
  color: var(--muted-foreground);
  margin-bottom: 3px;
`

const TextInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  background: ${overlayForegroundMix(6)};
  border: 1px solid var(--border);
  border-radius: 3px;
  padding: 4px 6px;
  color: var(--foreground);
  font-family: 'Manrope', sans-serif;
  font-size: 11px;
  outline: none;

  &:focus {
    border-color: var(--ring);
  }
  ${mobileInputTextStyles}
`

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 48px;
  resize: vertical;
  background: ${overlayForegroundMix(6)};
  border: 1px solid var(--border);
  border-radius: 3px;
  padding: 4px 6px;
  color: var(--foreground);
  font-family: 'Manrope', sans-serif;
  font-size: 11px;
  outline: none;

  &:focus {
    border-color: var(--ring);
  }
  ${mobileInputTextStyles}
`

const Field = styled.div`
  margin-bottom: 8px;
`

const GroupRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`

/**
 * The options differ only by hue, so selection has to read without colour: the
 * ring and the check are what carry it.
 */
const Swatch = styled.button<{ $background: string; $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  min-width: 22px;
  padding: 0;
  margin: 0;
  border-radius: 3px;
  background: ${({ $background }) => $background};
  border: 1px solid ${overlayForegroundMix(25)};
  box-shadow: ${({ $selected }) =>
    $selected
      ? '0 0 0 1px var(--background), 0 0 0 3px var(--foreground)'
      : 'none'};
  font-size: 12px;
  line-height: 1;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: 2px;
  }
`

/** The slash gives way to the check once it is selected, so the two never overlap. */
const NoneSwatch = styled(Swatch)`
  background-image: ${({ $selected }) =>
    $selected
      ? 'none'
      : `linear-gradient(
          to top right,
          transparent calc(50% - 1px),
          ${overlayForegroundMix(45)} calc(50% - 1px),
          ${overlayForegroundMix(45)} calc(50% + 1px),
          transparent calc(50% + 1px)
        )`};
`

const SwatchCheck = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
`

const PenList = styled.div`
  max-height: 120px;
  overflow-y: auto;
  border: 1px solid var(--border);
  border-radius: 3px;
  padding: 4px;
`

const PenRow = styled.label`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 2px 2px;
  cursor: pointer;
  color: var(--foreground);
  font-size: 11px;

  &:hover {
    background: ${overlayForegroundMix(6)};
  }
`

/**
 * Every geometry property here overrides the global `input` rule, which sizes
 * text fields (`width: 100%; height: 35px`) and would otherwise stretch a
 * checkbox across the whole row.
 */
const PenCheckbox = styled.input`
  width: 12px;
  height: 12px;
  min-width: 12px;
  padding: 0;
  margin: 0;
  border: none;
  border-radius: 0;
  overflow: visible;
  accent-color: #e8a33d;
  cursor: pointer;
  flex-shrink: 0;
`

const ColorDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

const PenName = styled.span<{ $offChart: boolean }>`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $offChart }) =>
    $offChart ? 'var(--muted-foreground)' : 'var(--foreground)'};
  font-style: ${({ $offChart }) => ($offChart ? 'italic' : 'normal')};
`

const AddPenButton = styled.button`
  flex-shrink: 0;
  margin-left: auto;
  width: 16px;
  height: 16px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 3px;
  background: ${overlayForegroundMix(8)};
  color: var(--muted-foreground);
  cursor: pointer;

  &:hover {
    background: ${overlayForegroundMix(20)};
    color: var(--foreground);
  }
`

const EmptyPens = styled.div`
  color: var(--muted-foreground);
  font-style: italic;
  padding: 2px;
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 10px;
`

const DeleteButton = styled(CantileverButton)`
  margin-right: auto;
`

/* ─── Component ──────────────────────────────────────────────────────────── */

interface LabelPanelValues {
  name: string
  description: string
  groupId: LabelGroupId | null
  sensorIds: string[]
}

/** The values a panel opens on, and the baseline a click-away is judged against. */
function seedValues(
  panel: LabelPanelState,
  chartPens: VisibleSensorInfo[]
): LabelPanelValues {
  return panel.mode === 'edit'
    ? {
        name: panel.label.name,
        description: panel.label.description,
        groupId: panel.label.groupId,
        sensorIds: panel.label.sensorIds,
      }
    : {
        name: '',
        description: '',
        groupId: null,
        sensorIds: chartPens.map((pen) => pen.sensorId),
      }
}

/** Scope is a set: unchecking a pen and rechecking it is not an edit. */
function sameSensorScope(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const inB = new Set(b)
  return a.every((sensorId) => inB.has(sensorId))
}

interface LabelConfigPanelProps {
  panel: LabelPanelState
  chartPens: VisibleSensorInfo[]
  isSaving: boolean
  onSave: (input: LabelPanelValues) => void
  /** Explicit Cancel — discards in both modes. */
  onCancel: () => void
  onDelete: () => void
  /** Plots a scoped pen the chart is not currently showing. */
  onAddPenToChart?: (sensorId: string) => void
}

export const LabelConfigPanel: React.FC<LabelConfigPanelProps> = ({
  panel,
  chartPens,
  isSaving,
  onSave,
  onCancel,
  onDelete,
  onAddPenToChart,
}) => {
  const { t } = useLingui()

  const isEdit = panel.mode === 'edit'
  const panelKey = isEdit ? panel.label.id : 'draft'
  const range = isEdit
    ? { startMs: panel.label.startMs, endMs: panel.label.endMs }
    : panel.range

  const penOptions = useMemo(
    () => buildPenScopeOptions(chartPens, isEdit ? panel.label.sensorIds : []),
    [chartPens, isEdit, panel]
  )

  const [seeded, setSeeded] = useState<LabelPanelValues>(() =>
    seedValues(panel, chartPens)
  )
  const [name, setName] = useState(seeded.name)
  const [description, setDescription] = useState(seeded.description)
  const [groupId, setGroupId] = useState<LabelGroupId | null>(seeded.groupId)
  const [sensorIds, setSensorIds] = useState<string[]>(seeded.sensorIds)

  // Re-seed when the panel switches to a different label (or from edit to a new
  // draft) without unmounting.
  const seededKeyRef = useRef(panelKey)
  useEffect(() => {
    if (seededKeyRef.current === panelKey) return
    seededKeyRef.current = panelKey
    const next = seedValues(panel, chartPens)
    setSeeded(next)
    setName(next.name)
    setDescription(next.description)
    setGroupId(next.groupId)
    setSensorIds(next.sensorIds)
  }, [chartPens, panel, panelKey])

  const panelRef = useRef<HTMLDivElement | null>(null)

  const handleSave = useCallback(() => {
    onSave({
      name: name.trim(),
      description: description.trim(),
      groupId,
      sensorIds,
    })
  }, [description, groupId, name, onSave, sensorIds])

  // Compared against the trimmed forms `handleSave` writes, so padding a value
  // with whitespace is not an edit.
  const isDirty =
    name.trim() !== seeded.name.trim() ||
    description.trim() !== seeded.description.trim() ||
    groupId !== seeded.groupId ||
    !sameSensorScope(sensorIds, seeded.sensorIds)

  /**
   * Click-away commits an edit (save-by-default) but never a draft: a new label
   * the user has not named yet has nothing worth persisting, so its panel stays
   * open until Save or Cancel resolves it. An untouched edit closes without
   * writing — every Rolodex write is audit-logged, so a stray click must not
   * make one.
   */
  const handleClickAway = useCallback(() => {
    if (isDirty) handleSave()
    else onCancel()
  }, [handleSave, isDirty, onCancel])

  // Read through a ref so the click-away listener is registered once instead of
  // on every keystroke.
  const handleClickAwayRef = useRef(handleClickAway)
  useEffect(() => {
    handleClickAwayRef.current = handleClickAway
  }, [handleClickAway])

  useEffect(() => {
    if (!isEdit) return

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (panelRef.current?.contains(target)) return
      handleClickAwayRef.current()
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [isEdit])

  const togglePen = useCallback((sensorId: string) => {
    setSensorIds((prev) =>
      prev.includes(sensorId)
        ? prev.filter((id) => id !== sensorId)
        : [...prev, sensorId]
    )
  }, [])

  return (
    <PanelAnchor>
      <Panel ref={panelRef} data-testid='label-config-panel'>
        <PanelHeader>
          <PanelTitle>{isEdit ? t`Edit label` : t`New label`}</PanelTitle>
        </PanelHeader>
        <TimeRange>
          {formatOverlayTimestamp(range.startMs)}
          {' — '}
          {formatOverlayTimestamp(range.endMs)}
        </TimeRange>

        <Field>
          <FieldLabel htmlFor='label-title-input'>
            <Trans>Title</Trans>
          </FieldLabel>
          <TextInput
            id='label-title-input'
            autoFocus
            value={name}
            placeholder={t`Label title`}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleSave()
            }}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor='label-description-input'>
            <Trans>Description</Trans>
          </FieldLabel>
          <TextArea
            id='label-description-input'
            value={description}
            placeholder={t`What happened in this window?`}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>

        <Field>
          <FieldLabel as='span' id='label-group-field'>
            <Trans>Group</Trans>
          </FieldLabel>
          <GroupRow role='group' aria-labelledby='label-group-field'>
            <NoneSwatch
              type='button'
              aria-label={t`None`}
              aria-pressed={groupId === null}
              $background={overlayForegroundMix(6)}
              $selected={groupId === null}
              onClick={() => setGroupId(null)}>
              {groupId === null && (
                <SwatchCheck $color='var(--foreground)'>✓</SwatchCheck>
              )}
            </NoneSwatch>
            {LABEL_GROUPS.map((group) => (
              <Swatch
                key={group.id}
                type='button'
                aria-label={group.name}
                aria-pressed={groupId === group.id}
                $background={group.accent}
                $selected={groupId === group.id}
                onClick={() => setGroupId(group.id)}>
                {groupId === group.id && (
                  <SwatchCheck $color='#0d0d0d'>✓</SwatchCheck>
                )}
              </Swatch>
            ))}
          </GroupRow>
        </Field>

        <Field>
          <FieldLabel as='span'>
            <Trans>Pens in scope</Trans>
          </FieldLabel>
          <PenList>
            {penOptions.length === 0 ? (
              <EmptyPens>
                <Trans>No pens on the chart</Trans>
              </EmptyPens>
            ) : (
              penOptions.map((option) => (
                <PenRow key={option.sensorId}>
                  <PenCheckbox
                    type='checkbox'
                    checked={sensorIds.includes(option.sensorId)}
                    onChange={() => togglePen(option.sensorId)}
                  />
                  {option.color && <ColorDot $color={option.color} />}
                  {option.onChart ? (
                    <PenName $offChart={false}>{option.name}</PenName>
                  ) : (
                    <>
                      <ChartTooltip
                        label={t`Scoped by this label but not on the chart`}
                        side='top'
                        style={{ flex: 1, minWidth: 0 }}>
                        <PenName $offChart>{option.name}</PenName>
                      </ChartTooltip>
                      {onAddPenToChart && (
                        <ChartTooltip
                          label={t`Add this pen to the chart`}
                          side='top'>
                          <AddPenButton
                            type='button'
                            aria-label={t`Add ${{ value523_49: option.name }} to the chart`}
                            onClick={(event) => {
                              // The row is a <label>; without this the click
                              // falls through and toggles the checkbox.
                              event.preventDefault()
                              onAddPenToChart(option.sensorId)
                            }}>
                            <Plus size={11} />
                          </AddPenButton>
                        </ChartTooltip>
                      )}
                    </>
                  )}
                </PenRow>
              ))
            )}
          </PenList>
        </Field>

        <Footer>
          {isEdit && (
            <DeleteButton
              ghost
              danger
              size='small'
              disabled={isSaving}
              onClick={onDelete}>
              <Trans>Delete</Trans>
            </DeleteButton>
          )}
          <CantileverButton
            ghost
            size='small'
            disabled={isSaving}
            onClick={onCancel}>
            <Trans>Cancel</Trans>
          </CantileverButton>
          <CantileverButton
            primary
            size='small'
            disabled={isSaving}
            onClick={handleSave}>
            <Trans>Save</Trans>
          </CantileverButton>
        </Footer>
      </Panel>
    </PanelAnchor>
  )
}
